import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import ThemeApplier from "@/components/ThemeApplier";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import AppLayout from "@/layouts/AppLayout";
import MagazzinoGenerale from "@/pages/MagazzinoGenerale";
import MagazzinoViaggiante from "@/pages/MagazzinoViaggiante";
import TecniciPage from "@/pages/TecniciPage";
import CommittentiPage from "@/pages/CommittentiPage";
import ListiniPage from "@/pages/ListiniPage";
import LavorazioniPage from "@/pages/LavorazioniPage";
import BefPage from "@/pages/BefPage";
import CedoliniPage from "@/pages/CedoliniPage";
import RimborsoKmPage from "@/pages/RimborsoKmPage";
import AgendaPage from "@/pages/AgendaPage";
import InstallScannerPage from "@/pages/InstallScannerPage";
import MaterialiPage from "@/pages/MaterialiPage";
import SettingsPage from "@/pages/SettingsPage";
import RankingPage from "@/pages/RankingPage";
import MagazziniPage from "@/pages/MagazziniPage";
import ClosuresPage from "@/pages/ClosuresPage";
import AutomezziPage from "@/pages/AutomezziPage";
import AttrezzaturaPage from "@/pages/AttrezzaturaPage";
import PreventiviPage from "@/pages/PreventiviPage";
import PerformancePage from "@/pages/PerformancePage";
import ManualePage from "@/pages/ManualePage";
import AssegnazioniImpianti from "@/pages/AssegnazioniImpianti";
import MieiAppuntamenti from "@/pages/MieiAppuntamenti";
import ProduzioneObiettivoPage from "@/pages/ProduzioneObiettivoPage";
import AccountEmailPage from "@/pages/AccountEmailPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import AbbonamentoPage from "@/pages/AbbonamentoPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import SubscriptionGate from "@/components/SubscriptionGate";
import SignupLandingPage from "@/pages/SignupLandingPage";
import DocumentiCondivisiPage from "@/pages/DocumentiCondivisiPage";
import MarketingLandingPage from "@/pages/MarketingLandingPage";
import OnboardingWizard from "@/components/OnboardingWizard";
import ScaricoMaterialiPage from "@/pages/ScaricoMaterialiPage";
import BulkDocumentiPage from "@/pages/BulkDocumentiPage";
import TecniciInCampoPage from "@/pages/TecniciInCampoPage";
import LetturaMailPage from "@/pages/LetturaMailPage";
import ProduzioneCommittentePage from "@/pages/ProduzioneCommittentePage";
import TotemPage from "@/pages/TotemPage";

function PublicRoot() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-slate-500">Caricamento...</div>;
  if (user) {
    if (user.role === "totem") return <Navigate to="/totem" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <MarketingLandingPage />;
}

function ProtectedRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-slate-500">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Il ruolo totem non usa il layout normale, va direttamente alla pagina fullscreen
  if (user.role === "totem") return <Navigate to="/totem" replace />;
  return <AppLayout><SubscriptionGate><OnboardingWizard /><Outlet /></SubscriptionGate></AppLayout>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function TotemGuard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center bg-slate-900 text-slate-300">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <TotemPage />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <AnalyticsTracker skip={[/^\/super-admin/, /^\/abbonamento/, /^\/subscription\//]} />
        <ThemeApplier />
        <Routes>
          <Route path="/" element={<PublicRoot />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/signup" element={<SignupLandingPage />} />
          <Route path="/totem" element={<TotemGuard />} />
          <Route element={<ProtectedRoutes />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/magazzino" element={<MagazzinoGenerale />} />
            <Route path="/magazzini" element={<MagazziniPage />} />
            <Route path="/viaggiante" element={<MagazzinoViaggiante />} />
            <Route path="/scarico-materiali" element={<ScaricoMaterialiPage />} />
            <Route path="/documenti-massivi" element={<BulkDocumentiPage />} />
            <Route path="/tecnici-in-campo" element={<TecniciInCampoPage />} />
            <Route path="/lettura-mail" element={<LetturaMailPage />} />
            <Route path="/produzione-committente" element={<AdminOnly><ProduzioneCommittentePage /></AdminOnly>} />
            <Route path="/materiali" element={<MaterialiPage />} />
            <Route path="/tecnici" element={<TecniciPage />} />
            <Route path="/committenti" element={<CommittentiPage />} />
            <Route path="/listini" element={<ListiniPage />} />
            <Route path="/lavorazioni" element={<LavorazioniPage />} />
            <Route path="/chiusure" element={<ClosuresPage />} />
            <Route path="/bef" element={<BefPage />} />
            <Route path="/cedolini" element={<CedoliniPage />} />
            <Route path="/rimborso-km" element={<RimborsoKmPage />} />
            <Route path="/agenda" element={<AgendaPage />} />
            <Route path="/ranking" element={<RankingPage />} />
            <Route path="/install" element={<InstallScannerPage />} />
            <Route path="/automezzi" element={<AutomezziPage />} />
            <Route path="/attrezzatura" element={<AttrezzaturaPage />} />
            <Route path="/preventivi" element={<PreventiviPage kind="preventivo" />} />
            <Route path="/bef-manuali" element={<PreventiviPage kind="bef" />} />
            <Route path="/performance" element={<PerformancePage />} />
            <Route path="/produzione-obiettivo" element={<ProduzioneObiettivoPage />} />
            <Route path="/manuale" element={<ManualePage />} />
            <Route path="/assegnazioni" element={<AssegnazioniImpianti />} />
            <Route path="/miei-appuntamenti" element={<MieiAppuntamenti />} />
            <Route path="/account-email" element={<AccountEmailPage />} />
            <Route path="/super-admin" element={<SuperAdminPage />} />
            <Route path="/abbonamento" element={<AbbonamentoPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/subscription/cancel" element={<AbbonamentoPage />} />
            <Route path="/impostazioni" element={<SettingsPage />} />
            <Route path="/documenti-condivisi" element={<DocumentiCondivisiPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
